import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { DemoBadge } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const ROLE_LABELS = {
  management_admin: "Management Admin", instructor: "Instructor / Assessor", funding_admin: "Funding / HR Admin",
  applicant: "Applicant", trainee: "Trainee", operator: "Operator",
};
const STAFF_ROLE_KEYS = ["management_admin", "instructor", "funding_admin"];

export default function StaffUsers() {
  const [staff, setStaff] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [form, setForm] = useState({ first_name: "", surname: "", email: "", password: "", role: "instructor" });
  const [saving, setSaving] = useState(false);
  const [changingRole, setChangingRole] = useState("");

  const load = () => {
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => toast.error("Could not load staff"));
    api.get("/admin/accounts").then((r) => setAccounts(r.data)).catch(() => toast.error("Could not load accounts"));
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Staff account created.");
      setForm({ first_name: "", surname: "", email: "", password: "", role: "instructor" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const changeRole = async (u, role) => {
    setChangingRole(u.id);
    try {
      await api.post(`/admin/users/${u.id}/role`, { role });
      toast.success(`${u.email} is now ${ROLE_LABELS[role]}.`);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setChangingRole(""); }
  };

  if (!staff || !accounts) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="staff-loading" />;

  const inputCls = "bg-obsidian border-steel text-charcoal h-11";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="staff-users-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Admin</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="staff-users-heading">Staff Accounts</h1>
        <p className="text-sm text-slate-500 mt-1">
          Staff roles are assigned internally. Candidates can only ever register as Applicants.
        </p>
      </div>

      <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="staff-list">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4">Current Staff</h2>
        <div className="space-y-2">
          {staff.map((u) => (
            <div key={u.id} className="flex items-center justify-between bg-obsidian rounded-sm px-4 py-3" data-testid={`staff-row-${u.id}`}>
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-sm font-semibold text-charcoal">{u.first_name} {u.surname}</p>
                  <p className="text-xs text-slate-500">{u.email}</p>
                </div>
                {u.is_demo && <DemoBadge />}
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-lomond">{ROLE_LABELS[u.role]}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="all-accounts-section">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-1">All Accounts</h2>
        <p className="text-xs text-slate-500 mb-4">
          Every authentication account — including candidate-role accounts that never appear in staff lists. Use this to spot hidden or orphaned accounts.
        </p>
        <div className="space-y-2">
          {accounts.map((u) => (
            <div key={u.id} className="bg-obsidian rounded-sm px-4 py-3" data-testid={`account-row-${u.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-charcoal truncate" data-testid={`account-name-${u.id}`}>{u.first_name} {u.surname}</p>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  </div>
                  {u.is_demo && <DemoBadge />}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={changingRole === u.id}>
                    <SelectTrigger className="h-9 w-44 bg-white border-steel text-charcoal text-xs" data-testid={`account-role-${u.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`account-role-${u.id}-${v}`}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-1.5 flex-wrap mt-2" data-testid={`account-links-${u.id}`}>
                <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500 bg-white border border-steel rounded-sm px-2 py-0.5">
                  Joined {String(u.created_at || "").slice(0, 10) || "—"}
                </span>
                {u.linked?.candidate_profile && (
                  <span className="text-[9px] font-mono uppercase tracking-widest text-slate-600 bg-white border border-steel rounded-sm px-2 py-0.5" data-testid={`link-applicant-${u.id}`}>Applicant profile</span>
                )}
                {u.linked?.trainee_profile && (
                  <span className="text-[9px] font-mono uppercase tracking-widest text-slate-600 bg-white border border-steel rounded-sm px-2 py-0.5" data-testid={`link-trainee-${u.id}`}>Trainee profile</span>
                )}
                {u.linked?.operator_profile && (
                  <span className="text-[9px] font-mono uppercase tracking-widest text-slate-600 bg-white border border-steel rounded-sm px-2 py-0.5" data-testid={`link-operator-${u.id}`}>Operator profile</span>
                )}
                {u.linked?.staff_profile && (
                  <span className="text-[9px] font-mono uppercase tracking-widest text-slate-700 bg-slate-100 border border-slate-300 rounded-sm px-2 py-0.5" data-testid={`link-staff-${u.id}`}>Staff profile</span>
                )}
                {u.linked?.admin_role && (
                  <span className="text-[9px] font-mono uppercase tracking-widest text-lomond bg-lomond/10 border border-lomond/30 rounded-sm px-2 py-0.5" data-testid={`link-admin-${u.id}`}>Admin role</span>
                )}
                {!u.linked?.candidate_profile && !u.linked?.trainee_profile && !u.linked?.operator_profile && !u.linked?.staff_profile && !u.linked?.admin_role && (
                  <span className="text-[9px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-300 rounded-sm px-2 py-0.5">No linked profile</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="create-staff-section">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4">Create Staff Account</h2>
        <form onSubmit={create} className="grid sm:grid-cols-2 gap-4" data-testid="create-staff-form">
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-sm">First name</Label>
            <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} data-testid="staff-first-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-sm">Surname</Label>
            <Input required value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className={inputCls} data-testid="staff-surname" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-sm">Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="staff-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-sm">Temporary password</Label>
            <Input required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="staff-password" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-sm">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className={inputCls} data-testid="staff-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAFF_ROLE_KEYS.map((v) => <SelectItem key={v} value={v}>{ROLE_LABELS[v]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={saving} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="create-staff-btn">
              <Plus className="h-4 w-4 mr-1.5" /> {saving ? "Creating..." : "Create Account"}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
